import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmDeleteDialog.css';

const ConfirmDeleteDialog = ({
  open,
  itemName = '',
  itemType = 'item',
  title,
  description = 'This action is permanent and cannot be undone.',
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !loading) onCancel?.();
    };
    document.addEventListener('keydown', onKeyDown);

    const scrollY = window.scrollY || 0;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [open, loading, onCancel]);

  if (!open) return null;

  const dialogTitle = title || `Delete ${itemType}?`;
  const displayName = String(itemName || '').trim();

  return createPortal(
    <div
      className="confirm-delete-backdrop"
      role="presentation"
      onClick={() => !loading && onCancel?.()}
    >
      <div
        className="confirm-delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-delete-icon-wrap" aria-hidden="true">
          <svg className="confirm-delete-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        </div>

        <h2 id="confirm-delete-title" className="confirm-delete-title">
          {dialogTitle}
        </h2>

        <p id="confirm-delete-desc" className="confirm-delete-message">
          {message ? (
            message
          ) : displayName ? (
            <>
              You are about to remove{' '}
              <strong className="confirm-delete-highlight">&ldquo;{displayName}&rdquo;</strong>
              {' '}from your {itemType} list.
            </>
          ) : (
            <>You are about to remove this {itemType}.</>
          )}
        </p>

        <p className="confirm-delete-warning">{description}</p>

        <div className="confirm-delete-actions">
          <button
            type="button"
            className="confirm-delete-btn confirm-delete-btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="confirm-delete-btn confirm-delete-btn-danger"
            onClick={onConfirm}
            disabled={loading}
            autoFocus
          >
            {loading ? (
              <>
                <span className="confirm-delete-spinner" aria-hidden="true" />
                Deleting...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDeleteDialog;
